import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

// Badges overlay on the WHITE design-preview thumbnail, so use solid dark
// pill backgrounds with white text — otherwise light emerald/sky/white text
// sits on a near-white surface and disappears. `jw-on-solid` opts these
// pills out of the light-theme `text-white -> navy` override so they stay
// legible in both themes.
const PRINT_TONE = {
  queued: 'jw-on-solid bg-slate-800/85 text-white border-white/20',
  printed: 'jw-on-solid bg-emerald-600 text-white border-emerald-700',
  shipped: 'jw-on-solid bg-sky-600 text-white border-sky-700',
  on_hold: 'jw-on-solid bg-amber-500 text-white border-amber-600',
};

export const CustomerDesignsSection = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [moderation, setModeration] = useState('all');
  const [printStatus, setPrintStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null); // opened design

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '24' });
      if (q.trim()) params.set('q', q.trim());
      if (moderation !== 'all') params.set('moderation_status', moderation);
      if (printStatus !== 'all') params.set('print_status', printStatus);
      const { data } = await api.get(`/admin/user-designs?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, moderation, printStatus]);

  const setStatus = async (id, next) => {
    try {
      await api.patch(`/admin/user-designs/${id}/print-status`, { print_status: next });
      toast.success('Print status updated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-customer-designs-section">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-400" /> Customer designs
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>
          Every design your customers have created — AI-generated, editor-built, or uploaded. Preview, download for print, moderate and track the print pipeline.
        </p>
      </div>

      <Card className="jw-card p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 opacity-60" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
              placeholder="Search by design name or customer email..."
              data-testid="admin-cd-search-input"
              className="flex-1"
            />
          </div>
          <Select value={moderation} onValueChange={(v) => { setModeration(v); setPage(1); }}>
            <SelectTrigger className="w-48" data-testid="admin-cd-moderation-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All moderation</SelectItem>
              <SelectItem value="pending">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={printStatus} onValueChange={(v) => { setPrintStatus(v); setPage(1); }}>
            <SelectTrigger className="w-48" data-testid="admin-cd-print-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All print statuses</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="printed">Printed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={() => { setPage(1); load(); }} disabled={loading} data-testid="admin-cd-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </Card>

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-cd-grid">
        {data.items.map((d) => (
          <Card key={d.id} className="jw-card overflow-hidden group" data-testid={`admin-cd-card-${d.id}`}>
            <div className="aspect-square overflow-hidden bg-white grid place-items-center relative">
              {d.preview_url ? (
                <img src={assetUrl(d.preview_url)} alt={d.name || 'Design'} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <Palette className="w-8 h-8 opacity-30" />
              )}
              {d.open_orders > 0 && (
                <span className="jw-on-solid absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-500 text-white shadow">
                  {d.open_orders} open order{d.open_orders === 1 ? '' : 's'}
                </span>
              )}
              <span className={`absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded-full border ${PRINT_TONE[d.print_status || 'queued'] || PRINT_TONE.queued}`}>
                {(d.print_status || 'queued').replace('_', ' ')}
              </span>
            </div>
            <div className="p-3 space-y-1">
              <p className="text-sm font-semibold truncate">{d.name || d.listing_title || 'Untitled'}</p>
              <p className="text-[11px] truncate" style={{ color: 'var(--jw-text-subtle)' }}>
                {(d.owner || {}).name || 'Unknown'} · {(d.owner || {}).email || '—'}
              </p>
              <div className="flex gap-1 pt-2">
                <a
                  href={d.preview_url ? assetUrl(d.preview_url) : '#'}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-cd-download-${d.id}`}
                  className="flex-1 text-[11px] text-center py-1.5 rounded-md jw-panel hover:opacity-80"
                >
                  <FileDown className="inline w-3 h-3 mr-1" /> Print file
                </a>
                <button
                  onClick={() => setDetail(d)}
                  data-testid={`admin-cd-open-${d.id}`}
                  className="flex-1 text-[11px] text-center py-1.5 rounded-md jw-panel hover:opacity-80"
                >
                  <Search className="inline w-3 h-3 mr-1" /> Details
                </button>
              </div>
              <Select value={d.print_status || 'queued'} onValueChange={(v) => setStatus(d.id, v)}>
                <SelectTrigger className="w-full mt-2 h-7 text-[11px]" data-testid={`admin-cd-status-${d.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="printed">Printed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="on_hold">On hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>
        ))}
        {!loading && data.items.length === 0 && (
          <div className="col-span-full text-center py-16" style={{ color: 'var(--jw-text-muted)' }}>
            No customer designs match those filters yet.
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-cd-prev-button">Prev</Button>
          <span className="self-center text-sm" style={{ color: 'var(--jw-text-muted)' }}>Page {data.page} / {data.pages}</span>
          <Button variant="secondary" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="admin-cd-next-button">Next</Button>
        </div>
      )}

      {/* Detail dialog */}
      {detail && (
        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="sm:max-w-2xl" data-testid="admin-cd-detail-dialog">
            <DialogHeader>
              <DialogTitle>{detail.name || 'Design'}</DialogTitle>
              <DialogDescription>
                Owned by {(detail.owner || {}).email || 'unknown'} · created {new Date(detail.created_at).toLocaleString()}
              </DialogDescription>
            </DialogHeader>
            <div className="grid sm:grid-cols-[220px_1fr] gap-4">
              <div className="rounded-lg overflow-hidden bg-white aspect-square grid place-items-center">
                {detail.preview_url ? <img src={assetUrl(detail.preview_url)} alt="" className="w-full h-full object-contain" /> : <Palette className="w-10 h-10 opacity-40" />}
              </div>
              <div className="space-y-2 text-sm">
                <p><b>Product type:</b> {detail.product_type || '—'}</p>
                <p><b>Moderation:</b> {detail.moderation_status || 'n/a'}</p>
                <p><b>Print status:</b> {detail.print_status || 'queued'}</p>
                {detail.open_orders > 0 && <p><b>Open orders:</b> {detail.open_orders}</p>}
                {detail.preview_url && (
                  <a href={assetUrl(detail.preview_url)} target="_blank" rel="noreferrer" className="inline-block mt-2 px-3 py-1.5 rounded-md jw-btn-glossy text-white text-xs">
                    <FileDown className="inline w-3 h-3 mr-1" /> Download print file
                  </a>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CustomerDesignsSection;
